import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import { API_ENDPOINT } from "../../services/api";
import { POST } from "../../constant/apiMethods";
import Labels from "../../services/apiLabels";
import { Line } from "react-chartjs-2";
import "./graph.css";

const POLL_MS = 5000;
const LINE = "#2563eb";
const LINE_DEEP = "#1d4ed8";
const GOLD = "#c4a052";

const money = (value) =>
  `€${Number(value || 0).toLocaleString(undefined, {
    maximumFractionDigits: 0,
  })}`;

// The API returns points as { x: "14.03.2022 15:19:04", y: 6000, name: "Bidder 1" }.
function normalizePoints(items) {
  if (!Array.isArray(items)) return [];
  return items
    .map((item) => ({
      x: String(item?.x ?? item?.date ?? ""),
      y: Number(item?.y ?? item?.bid_amount ?? 0),
      name: item?.name ?? item?.user ?? "",
    }))
    .filter((point) => Number.isFinite(point.y));
}

// Axis ticks only have room for day and time; tooltips keep the full stamp.
function shortLabel(value) {
  const [date = "", time = ""] = String(value).split(" ");
  const dayMonth = date.split(".").slice(0, 2).join(".");
  const hourMinute = time.split(":").slice(0, 2).join(":");
  return [dayMonth, hourMinute].filter(Boolean).join(" ") || String(value);
}

function areaGradient(context) {
  const { ctx, chartArea } = context.chart;
  if (!chartArea) return "rgba(37, 99, 235, 0.16)";
  const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
  gradient.addColorStop(0, "rgba(37, 99, 235, 0.28)");
  gradient.addColorStop(1, "rgba(37, 99, 235, 0)");
  return gradient;
}

export default forwardRef((props, ref) => {
  const { user_id, auction_id, graphDataCb } = props;

  const [points, setPoints] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const timerRef = useRef(null);
  const mountedRef = useRef(true);
  const callbackRef = useRef(graphDataCb);
  callbackRef.current = graphDataCb;

  useEffect(
    () => () => {
      mountedRef.current = false;
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    []
  );

  const fetchGraphDataHandler = useCallback(async () => {
    if (!auction_id) return;
    try {
      const response = await fetch(API_ENDPOINT(Labels.bid_graph), {
        method: POST,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ p_id: auction_id, user_id: String(user_id) }),
      });
      const result = await response.json();
      if (!mountedRef.current) return;
      if (result?.data) {
        if (typeof callbackRef.current === "function") {
          callbackRef.current(result.data);
        }
        setPoints(normalizePoints(result.data.items));
      }
    } catch (e) {
      // Polling keeps running; a single failed refresh should stay silent.
    } finally {
      if (mountedRef.current) {
        setLoaded(true);
        timerRef.current = setTimeout(fetchGraphDataHandler, POLL_MS);
      }
    }
  }, [auction_id, user_id]);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    fetchGraphDataHandler();
  }, [fetchGraphDataHandler]);

  useImperativeHandle(ref, () => ({
    fetchGraphData() {
      if (timerRef.current) clearTimeout(timerRef.current);
      fetchGraphDataHandler();
    },
  }));

  const data = useMemo(
    () => ({
      labels: points.map((point) => shortLabel(point.x)),
      datasets: [
        {
          label: "Bid amount",
          data: points.map((point) => point.y),
          borderColor: LINE,
          borderWidth: 2.5,
          backgroundColor: areaGradient,
          fill: true,
          tension: 0.35,
          pointRadius: points.length > 60 ? 0 : 4,
          pointHoverRadius: 7,
          pointBackgroundColor: "#fff",
          pointBorderColor: LINE_DEEP,
          pointBorderWidth: 2,
          pointHoverBackgroundColor: GOLD,
          pointHoverBorderColor: GOLD,
        },
      ],
    }),
    [points]
  );

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "nearest", intersect: false },
      layout: { padding: { top: 8, right: 8 } },
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: "rgba(15, 23, 42, 0.94)",
          padding: 12,
          cornerRadius: 10,
          displayColors: false,
          titleFont: { size: 11, weight: "600" },
          bodyFont: { size: 13, weight: "700" },
          callbacks: {
            title: (items) => points[items?.[0]?.dataIndex]?.x ?? "",
            label: (item) => money(item?.parsed?.y),
            afterLabel: (item) => {
              const name = points[item?.dataIndex]?.name;
              return name ? `Bidder: ${name}` : "";
            },
          },
        },
      },
      scales: {
        x: {
          grid: { display: false },
          border: { display: false },
          offset: true,
          ticks: {
            color: "#94a3b8",
            font: { size: 11 },
            maxRotation: 0,
            autoSkip: true,
            maxTicksLimit: 8,
          },
        },
        y: {
          grid: { color: "rgba(15, 23, 42, 0.06)" },
          border: { display: false },
          ticks: {
            color: "#94a3b8",
            font: { size: 11 },
            padding: 8,
            callback: (value) => money(value),
          },
        },
      },
    }),
    [points]
  );

  const summary = useMemo(() => {
    if (!points.length) return null;
    const values = points.map((point) => point.y);
    return {
      count: points.length,
      high: Math.max(...values),
      open: values[0],
    };
  }, [points]);

  if (!points.length) {
    return (
      <div className="dp-graph dp-graph--empty">
        <p>{loaded ? "No bids placed yet." : "Loading bid history…"}</p>
      </div>
    );
  }

  return (
    <div className="dp-graph">
      <div className="dp-graph__summary">
        <span>
          <strong>{summary.count}</strong> bids
        </span>
        <span>
          Opening <strong>{money(summary.open)}</strong>
        </span>
        <span>
          Highest <strong>{money(summary.high)}</strong>
        </span>
      </div>
      <div className="dp-graph__canvas">
        <Line type="line" data={data} options={options} />
      </div>
    </div>
  );
});
